import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Environment, EnvironmentService } from '@galaxy/core';
import { Account as A, AccountPlan, AccountService } from '@vendasta/constant-contact';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { constantContactAppId } from '../../marketplace-app/global-brands-constants';
import { ChangePlanData, ChangePlanDialogComponent, PlanOption, PlanOptionValue } from './change-plan-dialog.component';

const upgradeableProductIds: string[] = [
  constantContactAppId[Environment.PROD],
  constantContactAppId[Environment.DEMO],
];
export type Account = A;

@Injectable({ providedIn: 'root' })
export class ChangePlanService {
  private changePlanDialog: MatDialogRef<ChangePlanDialogComponent>;
  constructor(
    private accountService: AccountService,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    private environmentService: EnvironmentService,
  ) {}

  isUpgradableProduct(productId: string): boolean {
    return upgradeableProductIds.some((v) => v === productId);
  }

  openChangePlanDialog(partnerId: string, accountGroupId: string, productId: string): void {
    let changePlanData: ChangePlanData;
    const env = this.environmentService.getEnvironment();
    switch (productId) {
      case constantContactAppId[env]:
        changePlanData = this.constantContactChangePlanData(partnerId, accountGroupId, productId);
        break;
      default:
        this.snackbarService.errorSnack('An unexpected error occurred.');
        return;
    }
    this.openDialog(changePlanData);
  }

  private constantContactChangePlanData(partnerId: string, accountGroupId: string, productId: string): ChangePlanData {
    return {
      moreDetailsMessageHTML:
        'Full plan details are available on the ' +
        '<a target="_blank" href="marketplace/products/' +
        productId +
        '">' +
        'Constant Contact details page.' +
        '</a>' +
        '<br> Your next invoice might not reflect this change.',
      options: [
        {
          name: this.getPlanName(AccountPlan.ACCOUNT_PLAN_LITE),
          value: AccountPlan.ACCOUNT_PLAN_LITE,
        },
        {
          name: this.getPlanName(AccountPlan.ACCOUNT_PLAN_STANDARD),
          value: AccountPlan.ACCOUNT_PLAN_STANDARD,
        },
        {
          name: this.getPlanName(AccountPlan.ACCOUNT_PLAN_PREMIUM),
          value: AccountPlan.ACCOUNT_PLAN_PREMIUM,
        },
      ],
      currentPlan$: this.getConstantContactAccountPlan(accountGroupId),
      onSubmit: (planOptionValue) => {
        this.handleConstantContactSubmission(partnerId, accountGroupId, planOptionValue);
      },
    };
  }

  private openDialog(changePlanData: ChangePlanData): void {
    this.changePlanDialog = this.dialog.open(ChangePlanDialogComponent, {
      maxWidth: '600px',
      minHeight: '200px',
      minWidth: '400px',
      data: changePlanData,
    });
  }

  private handleConstantContactSubmission(partnerId: string, accountGroupId: string, plan: PlanOptionValue): void {
    if (this.isValidConstantContactPlan(plan)) {
      this.updateConstantContactPlan(partnerId, accountGroupId, plan as AccountPlan);
    } else {
      this.snackbarService.errorSnack('An error occurred. Please try again');
    }
  }

  private updateConstantContactPlan(partnerId: string, accountGroupId: string, plan: AccountPlan): void {
    this.accountService.updateAccountPlan(partnerId, accountGroupId, plan).subscribe(
      () => {
        this.snackbarService.successSnack('Working. Changes will be complete in 1 minute', null, 20000);
      },
      () => {
        this.snackbarService.errorSnack('An error occurred. Please try again');
      },
    );
  }

  private getConstantContactAccountPlan(accountGroupId: string): Observable<PlanOption> {
    return this.accountService.getAccount(accountGroupId).pipe(
      map((account) => {
        if (!account) {
          return { name: 'unknown', value: AccountPlan.ACCOUNT_PLAN_INVALID };
        }
        return {
          name: this.getPlanName(account.plan),
          value: account.plan,
        };
      }),
      catchError(() => {
        return observableOf({
          name: 'unknown',
          value: AccountPlan.ACCOUNT_PLAN_INVALID,
        });
      }),
    );
  }

  private isValidConstantContactPlan(val: PlanOptionValue): boolean {
    return [
      AccountPlan.ACCOUNT_PLAN_LITE,
      AccountPlan.ACCOUNT_PLAN_STANDARD,
      AccountPlan.ACCOUNT_PLAN_PREMIUM,
    ].includes(val);
  }

  private getPlanName(plan: PlanOptionValue): string {
    switch (plan) {
      case AccountPlan.ACCOUNT_PLAN_EMAIL:
        return 'Email';
      case AccountPlan.ACCOUNT_PLAN_EMAIL_PLUS:
        return 'Email Plus';
      case AccountPlan.ACCOUNT_PLAN_LITE:
        return 'Lite';
      case AccountPlan.ACCOUNT_PLAN_STANDARD:
        return 'Standard';
      case AccountPlan.ACCOUNT_PLAN_PREMIUM:
        return 'Premium';
      default:
        return 'unknown';
    }
  }
}
